'use client';

import { Close, UploadPhotos } from '@ahamove/icons';
import { compressImage } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import { ActionIcon, Avatar, Box, Group, rem } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { X as IconClose } from 'lucide-react';
import type { ReactNode } from 'react';
import useUploadOrderImage from './api/use-upload-image';

export type UploadPhotoProps = {
  maxPhotoUpload?: number;
  iconAddPhoto?: ReactNode;
  images: string[];
  onChange: (selectedImages: string[], index?: number) => void;
  dropzoneStyles?: React.CSSProperties;
  index?: number;
};

export const DEFAULT_MAX_PHOTO_UPLOAD = 5;

export function UploadPhoto({
  maxPhotoUpload = DEFAULT_MAX_PHOTO_UPLOAD,
  images,
  onChange,
  dropzoneStyles,
  iconAddPhoto,
  index,
}: UploadPhotoProps) {
  const { mutate: uploadImage, isPending: isLoadingImage } =
    useUploadOrderImage({
      onSuccess(data) {
        if (data?.url) {
          onChange([...images, data.url], index);
        }
      },
      onError(error) {
        toast.error({
          message:
            error?.response?.data.description ??
            error?.response?.data.title ??
            'Upload image failed',
          withIcon: true,
        });
      },
    });

  const handleDropFile = async (files: File[]) => {
    try {
      if (files && files.length > 0) {
        const compressedFile = await compressImage(files[0]);
        uploadImage({
          file: compressedFile,
          fileType: 'order_proof',
        });
      }
    } catch (error) {
      toast.error({
        message: ' Error selection file. Please choose another file!',
        withIcon: true,
      });
    }
  };

  const handleRemovePhoto = (imageUrl: string) => {
    const newImages = images.filter((img) => img !== imageUrl);
    onChange(newImages, index);
  };

  return (
    <Group gap="sm">
      {images && images.length > 0 ? (
        <>
          {images.map((imageUrl, idx) => (
            <PhotoPreviewItem
              key={idx}
              imageUrl={imageUrl}
              onRemove={handleRemovePhoto}
            />
          ))}
        </>
      ) : null}
      {images && images?.length < maxPhotoUpload ? (
        <Dropzone
          onDrop={(files) => handleDropFile(files)}
          loading={isLoadingImage}
          loaderProps={{
            size: 'md',
          }}
          maxFiles={1}
          accept={IMAGE_MIME_TYPE}
          multiple={false}
          styles={{
            root: { border: 'none', position: 'relative' },
            ...dropzoneStyles,
          }}
          style={{
            minHeight: 56,
            maxWidth: 60,
            padding: 0,
          }}
        >
          <Dropzone.Idle>
            {iconAddPhoto ? (
              iconAddPhoto
            ) : (
              <ActionIcon bg="neutral.1" w={56} h={56}>
                <UploadPhotos width={18} height={18} />
              </ActionIcon>
            )}
          </Dropzone.Idle>

          <Dropzone.Reject>
            <Close
              style={{
                width: rem(52),
                height: rem(52),
                color: 'red.6',
              }}
              stroke="1.5"
            />
          </Dropzone.Reject>

          <Dropzone.Accept>
            {iconAddPhoto ? (
              iconAddPhoto
            ) : (
              <ActionIcon bg="neutral.1" w={56} h={56}>
                <UploadPhotos />
              </ActionIcon>
            )}
          </Dropzone.Accept>
        </Dropzone>
      ) : null}
    </Group>
  );
}

export const PhotoPreviewItem = ({
  imageUrl,
  onRemove,
}: {
  imageUrl: string;
  onRemove: (imageUrl: string) => void;
}) => (
  <Box pos="relative">
    <ActionIcon
      size={18}
      variant="transparent"
      pos="absolute"
      top={-9}
      right={-9}
      bg="neutral.1"
      radius="lg"
      p="xs"
      onClick={() => onRemove(imageUrl)}
      style={{ outline: 'none !important', zIndex: 1 }}
    >
      <IconClose
        style={{
          position: 'absolute',
          width: 10,
          height: 10,
          color: 'black',
        }}
      />
    </ActionIcon>

    <Avatar radius="sm" src={imageUrl} alt="image note" size="lg" />
  </Box>
);
